import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { tr, de, enUS } from 'date-fns/locale';
import {
  CheckCircle, Calendar, User, Mail, Phone,
  Building2, CreditCard, Download, Home, Loader2, Users, Coffee
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import api from '../lib/api';

const HotelBookingSuccessPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bookingId = searchParams.get('id') || searchParams.get('order_id');

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    } else {
      const storedBooking = sessionStorage.getItem('completedBooking');
      if (storedBooking) {
        setBooking(JSON.parse(storedBooking));
        setLoading(false);
        cleanupSession();
      } else {
        setError('Rezervasyon bilgisi bulunamadı');
        setLoading(false);
      }
    }
  }, [bookingId]);

  const cleanupSession = () => {
    sessionStorage.removeItem('selectedHotelOffer');
    sessionStorage.removeItem('pendingHotelBookingId');
  };

  const fetchBookingDetails = async () => {
    try {
      const response = await api.get(`/hotel-booking/${bookingId}`);
      if (response.data.success) {
        setBooking(response.data.booking);
        cleanupSession();
      } else {
        setError('Rezervasyon bulunamadı');
      }
    } catch (err) {
      console.error('Error fetching booking:', err);
      const storedBooking = sessionStorage.getItem('completedBooking');
      if (storedBooking) {
        setBooking(JSON.parse(storedBooking));
        cleanupSession();
      } else {
        setError('Rezervasyon detayları alınamadı');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={() => navigate('/hotels')}>
              <Home className="w-4 h-4 mr-2" />
              {t('hotelBooking.backToHotels') || 'Otellere Dön'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hotelName = booking?.hotel_name || booking?.hotel || '';
  const checkIn = booking?.check_in || booking?.checkIn || '';
  const checkOut = booking?.check_out || booking?.checkOut || '';
  const roomName = booking?.room?.name || booking?.room || '';
  const boardName = booking?.room?.board || '';
  const guestName = booking?.guest
    ? `${booking.guest.first_name || ''} ${booking.guest.last_name || ''}`.trim()
    : '';
  const guestEmail = booking?.guest?.email || '';
  const guestPhone = booking?.guest?.phone || '';
  const nights = Number(booking?.nights || 0);
  const adults = Number(booking?.adults ?? 2);
  const children = Number(booking?.children ?? 0);
  const rooms = Number(booking?.rooms ?? 1);
  const totalEur = Number(booking?.pricing?.total_price_eur || booking?.totalEur || 0);
  const totalTry = Number(booking?.pricing?.total_price_try || booking?.totalTry || 0);
  const reservationId = booking?.id || booking?.bookingId || bookingId;
  const emailSent = Boolean(booking?.confirmation_email_sent);
  const paymentStatus = booking?.payment?.status || '';
  const bookingStatus = booking?.booking_status || '';
  const isFullyConfirmed =
    paymentStatus === 'completed' && bookingStatus === 'confirmed';

  if (!isFullyConfirmed && bookingId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-amber-700 font-medium">
              {t('hotelBooking.paymentPendingTitle') || 'Ödeme henüz onaylanmadı'}
            </p>
            <p className="text-sm text-gray-600">
              {t('hotelBooking.paymentPendingDesc') || 'Rezervasyon kaydı oluştu ancak banka ödemesini onaylamadı. Lütfen kart bakiyenizi kontrol edip tekrar deneyin veya destek ile iletişime geçin.'}
            </p>
            <p className="text-xs text-gray-500">#{reservationId}</p>
            <Button onClick={() => navigate('/hotels/booking')} className="w-full">
              {t('hotelBooking.retryPayment') || 'Ödemeyi Tekrar Dene'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/hotels')} className="w-full">
              {t('hotelBooking.backToHotels') || 'Otellere Dön'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 print:py-4">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-green-800 mb-2">
            {t('hotelBooking.successTitle') || 'Rezervasyonunuz Tamamlandı!'}
          </h1>
          <p className="text-gray-600">
            {emailSent
              ? (t('hotelBooking.successMessage') || 'Rezervasyon onayınız e-posta adresinize gönderildi.')
              : (t('hotelBooking.successMessagePending') || 'Ödemeniz alındı. Onay e-postası kısa süre içinde gönderilecektir.')}
          </p>
        </div>

        <Card className="mb-6 shadow-lg print:shadow-none">
          <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-t-lg print:bg-indigo-600">
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              <span>{t('hotelBooking.reservationDetails') || 'Rezervasyon Detayları'}</span>
              <span className="text-sm font-normal bg-white/20 px-3 py-1 rounded-full">
                #{reservationId}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="flex items-start gap-4 pb-4 border-b">
              <Building2 className="w-6 h-6 text-indigo-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg">{hotelName}</h3>
                {roomName && <p className="text-gray-600">{roomName}</p>}
                {boardName && (
                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                    <Coffee className="w-3.5 h-3.5" />
                    {boardName}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pb-4 border-b">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="text-sm text-gray-500">{t('hotelBooking.checkIn') || 'Giriş'}</p>
                  <p className="font-medium">
                    {checkIn && format(new Date(checkIn), 'd MMM yyyy', { locale: getDateLocale() })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="text-sm text-gray-500">{t('hotelBooking.checkOut') || 'Çıkış'}</p>
                  <p className="font-medium">
                    {checkOut && format(new Date(checkOut), 'd MMM yyyy', { locale: getDateLocale() })}
                  </p>
                </div>
              </div>
            </div>

            {nights > 0 && (
              <div className="flex items-center gap-3 pb-4 border-b text-sm">
                <Users className="w-5 h-5 text-indigo-600" />
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span>
                    {nights} {t('hotelBooking.nightsText') || 'gece'}
                  </span>
                  <span>
                    {adults} {t('hotelBooking.adults') || 'yetişkin'}
                    {children > 0 ? `, ${children} ${t('hotelBooking.children') || 'çocuk'}` : ''}
                  </span>
                  <span>
                    {rooms} {t('hotelBooking.rooms') || 'oda'}
                  </span>
                </div>
              </div>
            )}

            {guestName && (
              <div className="space-y-3 pb-4 border-b">
                <h4 className="font-medium text-gray-700">{t('hotelBooking.guestInfo') || 'Misafir Bilgileri'}</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-sm">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>{guestName}</span>
                  </div>
                  {guestEmail && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span>{guestEmail}</span>
                    </div>
                  )}
                  {guestPhone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{guestPhone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <h4 className="font-medium text-gray-700">{t('hotelBooking.paymentInfo') || 'Ödeme Bilgileri'}</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-green-600 font-medium">
                      {t('hotelBooking.paymentCompleted') || 'Ödeme Tamamlandı'}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-indigo-600">€{totalEur.toFixed(2)}</p>
                    {totalTry > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        {t('hotelBooking.chargedTry') || 'Kartınızdan tahsil'}: ₺{totalTry.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
          <Button
            variant="outline"
            onClick={() => window.print()}
            className="flex items-center gap-2"
            data-testid="print-confirmation-btn"
          >
            <Download className="w-4 h-4" />
            {t('hotelBooking.printConfirmation') || 'Onayı Yazdır'}
          </Button>
          <Button
            onClick={() => navigate('/hotels')}
            className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
            data-testid="back-to-hotels-btn"
          >
            <Home className="w-4 h-4" />
            {t('hotelBooking.backToHotels') || 'Otellere Dön'}
          </Button>
        </div>

        <div className="mt-8 text-center text-sm text-gray-500">
          <p>{t('hotelBooking.contactSupport') || 'Sorularınız için: support@turkishemarket.com'}</p>
        </div>
      </div>
    </div>
  );
};

export default HotelBookingSuccessPage;
